import {
  iMessage,
  iMessageAttachment,
} from "@sdk/conversations/conversations.models";
import {
  getWACallLogDisplayStatus,
  type iWACallLog,
} from "@sdk/wa-call-logs/wa-call-logs-model";
import { Card, Typography } from "antd";
import { LoadingIndicatorWithoutSpin } from "components/common/loading-indicator/loading-indicator";
import { useEffect, useRef } from "react";
import { loadWACallLogById } from "store/modules/wa-call-logs/wa-call-logs.helpers";
import { selectWACallLogById } from "store/modules/wa-call-logs/wa-call-logs.selectors";
import { useQueryWithStore } from "store/store.hooks";
import { GlobalEventEmitter } from "utils/global-event-emitter";
import { isWACallLogActive } from "./call-status";

const { Text } = Typography;

// eslint-disable-next-line react-refresh/only-export-components
export const INITATE_WA_CALL_EVENT = "INITATE_WA_CALL";

export interface InitiateWACallPayload {
  conversationId: string;
  messageId: string;
  browserId: string;
}

function formatDuration(ms: number | undefined): string {
  if (ms == null) return "";
  const m = Math.floor(ms / 60000);
  const s = Math.floor((ms % 60000) / 1000)
    .toString()
    .padStart(2, "0");
  return `${m}:${s}`;
}

function getDirectionLabel(
  callLog: iWACallLog,
  isOutgoingFromPayload: boolean,
): string {
  if (isOutgoingFromPayload) return "Outgoing";
  if (callLog.startedSource === "USER") return "Outgoing";
  if (callLog.startedSource === "CUSTOMER") return "Incoming";
  if (
    callLog.callStatus === "OUTGOING_INIT" ||
    callLog.callStatus === "OUTGOING_INIT_SUCCESS" ||
    callLog.callStatus === "RINGING"
  ) {
    return "Outgoing";
  }
  return "Incoming";
}

/**
 * Read-only WhatsApp call attachment for the message list.
 * Shows direction, state, and duration only. No WebRTC or call controls.
 * Active calls are handled by FloatingWhatsAppCallContainer in the nav bar.
 */
export const WhatsappCallAttachmentReadOnly = ({
  attachment,
  message,
}: {
  attachment: iMessageAttachment;
  message: iMessage;
}) => {
  const callLogId = (attachment.payload?.callLogId as string) ?? "";
  const isOutgoingFromPayload = !!(attachment.payload?.outgoingCall as boolean);
  const browserId = (attachment.payload?.browserId as string) ?? "";
  const hasEmittedInitiateRef = useRef(false);

  const { state: waCallLog, isLoading } = useQueryWithStore(
    selectWACallLogById(callLogId),
    loadWACallLogById(callLogId)(),
    [callLogId],
    !callLogId,
  );

  // Outgoing call before call log exists: signal floating container to initiate
  useEffect(() => {
    if (
      !isOutgoingFromPayload ||
      callLogId ||
      !message.conversationId ||
      !message.id ||
      hasEmittedInitiateRef.current ||
      browserId !== (window as any).browserSessionId
    ) {
      return;
    }
    hasEmittedInitiateRef.current = true;
    GlobalEventEmitter.emit(INITATE_WA_CALL_EVENT, {
      conversationId: message.conversationId,
      messageId: message.id,
      browserId,
    } as InitiateWACallPayload);
  }, [
    isOutgoingFromPayload,
    callLogId,
    message.conversationId,
    message.id,
    browserId,
  ]);

  // Outgoing with no callLogId yet: show initiating or cancelled
  if (isOutgoingFromPayload && !callLogId) {
    const isThisSession =
      browserId ===
      (typeof window !== "undefined" && (window as any).browserSessionId);
    if (!isThisSession) {
      return (
        <Card size="small" className="mb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <i className="ri-phone-line text-red-500" />
              <Text type="secondary">Call cancelled</Text>
            </div>
          </div>
        </Card>
      );
    }
    return (
      <Card size="small" className="mb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <i className="ri-phone-line text-orange-500" />
            <Text type="secondary">Outgoing · Initiating</Text>
          </div>
        </div>
        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Use the call panel to start the call.
        </div>
      </Card>
    );
  }

  if (!callLogId) return null;
  if (isLoading) {
    return (
      <Card size="small" className="mb-2">
        <LoadingIndicatorWithoutSpin />
      </Card>
    );
  }
  if (!waCallLog) {
    return (
      <Card size="small" className="mb-2">
        <Text type="secondary">Call log unavailable</Text>
      </Card>
    );
  }

  const direction = getDirectionLabel(waCallLog, isOutgoingFromPayload);
  const statusLabel = getWACallLogDisplayStatus(waCallLog);
  const duration = formatDuration(waCallLog.duration);
  const isActive = isWACallLogActive(waCallLog);

  return (
    <Card size="small" className="mb-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <i
            className={`ri-phone-line ${
              isActive ? "text-green-500" : "text-red-500"
            }`}
          />
          <Text type="secondary">
            {direction} · {statusLabel}
          </Text>
        </div>
        {duration ? (
          <Text type="secondary" className="text-xs ml-2">
            {duration}
          </Text>
        ) : null}
      </div>
      {isActive && (
        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Use the call panel to answer or control the call.
        </div>
      )}
    </Card>
  );
};
